import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { resolveImageUrl } from "../../utils/ImageUrl";

export type ImageStage =
  | "promo"
  | "members"
  | "services"
  | "stats"
  | "appointment"
  | "tagline"
  | "beforeAfter";

// Load priority: an image group only starts downloading once every earlier
// group has finished. "promo" is special — it advances after its FIRST image
// so the rest of the page can begin while the slideshow keeps loading.
const STAGE_ORDER: ImageStage[] = [
  "promo",
  "members",
  "services",
  "stats",
  "appointment",
  "tagline",
  "beforeAfter",
];

const indexOfStage = (stage: ImageStage): number => STAGE_ORDER.indexOf(stage);

// If a stage's images never report completion (broken/non-image URLs that
// don't fire load or error), advance anyway after this long so the cascade
// can't permanently stall and hide later sections.
const STALL_TIMEOUT_MS = 5000;

const zeroRecord = <T,>(value: T): Record<ImageStage, T> => ({
  promo: value,
  members: value,
  services: value,
  stats: value,
  appointment: value,
  tagline: value,
  beforeAfter: value,
});

interface StagedImageContextValue {
  isStageActive: (stage: ImageStage) => boolean;
  registerStage: (stage: ImageStage) => void;
  setStageCount: (
    stage: ImageStage,
    count: number,
    completeAfter?: number,
  ) => void;
  reportImageLoaded: (stage: ImageStage) => void;
}

const StagedImageContext = createContext<StagedImageContextValue | null>(null);

export const useStagedImage = (): StagedImageContextValue => {
  const ctx = useContext(StagedImageContext);
  if (!ctx) {
    throw new Error("useStagedImage must be used within a StagedImageProvider");
  }
  return ctx;
};

export const StagedImageProvider = ({ children }: { children: ReactNode }) => {
  const [level, setLevel] = useState(0);
  const present = useRef<Set<ImageStage>>(new Set());
  const total = useRef<Record<ImageStage, number>>(zeroRecord(0));
  const done = useRef<Record<ImageStage, number>>(zeroRecord(0));
  const completeAfter = useRef<Record<ImageStage, number>>(zeroRecord(0));
  const completed = useRef<Record<ImageStage, boolean>>(zeroRecord(false));
  const counted = useRef<Record<ImageStage, boolean>>(zeroRecord(false));

  const nextPresentIndex = useCallback((from: number): number => {
    for (let i = from; i < STAGE_ORDER.length; i += 1) {
      if (present.current.has(STAGE_ORDER[i])) return i;
    }
    return from;
  }, []);

  const tryComplete = useCallback(
    (stage: ImageStage) => {
      const idx = indexOfStage(stage);
      if (idx < 0) return;
      if (!completed.current[stage]) {
        // A cached image can report before setStageCount registers the true
        // counts; without this guard `need` would be 0 and the stage would
        // complete prematurely with an incomplete picture of the page.
        if (!counted.current[stage]) return;
        const need = Math.min(
          completeAfter.current[stage],
          total.current[stage],
        );
        if (done.current[stage] < need) return;
        completed.current[stage] = true;
      }
      // Advance even when the stage completed earlier (e.g. an empty section
      // finished while the cascade was still on a previous stage). Otherwise
      // landing on that stage would stall here and no later section's images
      // would ever download.
      setLevel((cur) => (cur === idx ? nextPresentIndex(idx + 1) : cur));
    },
    [nextPresentIndex],
  );

  const registerStage = useCallback((stage: ImageStage) => {
    present.current.add(stage);
  }, []);

  const setStageCount = useCallback(
    (stage: ImageStage, count: number, after = count) => {
      total.current[stage] = count;
      completeAfter.current[stage] = count === 0 ? 0 : after;
      counted.current[stage] = true;
      tryComplete(stage);
    },
    [tryComplete],
  );

  const reportImageLoaded = useCallback(
    (stage: ImageStage) => {
      done.current[stage] += 1;
      tryComplete(stage);
    },
    [tryComplete],
  );

  const isStageActive = useCallback(
    (stage: ImageStage): boolean => indexOfStage(stage) <= level,
    [level],
  );

  // On mount, jump straight to the first stage that actually has images on
  // this page (so sub-pages that only show, say, the gallery don't stall).
  // Never move the level backwards: a cached image can report during the
  // commit phase (before effects run), and rewinding here would strand the
  // cascade because a completed stage never re-advances the level.
  useEffect(() => {
    setLevel((cur) => Math.max(cur, nextPresentIndex(0)));
  }, [nextPresentIndex]);

  // When the active stage changes, immediately skip any stage that has no
  // images left to load (e.g. an empty section).
  useEffect(() => {
    tryComplete(STAGE_ORDER[level]);
  }, [level, tryComplete]);

  // Safety net: a broken image that never fires load/error (e.g. a URL that
  // resolves to a non-image response) would otherwise stall the whole cascade
  // and stop later sections from ever downloading. If the current stage hasn't
  // completed within the timeout, force it through so the next stage can start.
  useEffect(() => {
    const stage = STAGE_ORDER[level];
    if (!stage || completed.current[stage]) return;
    const timer = window.setTimeout(() => {
      if (!completed.current[stage]) {
        completed.current[stage] = true;
        setLevel((cur) => (cur === level ? nextPresentIndex(level + 1) : cur));
      }
    }, STALL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [level, nextPresentIndex]);

  const value: StagedImageContextValue = {
    isStageActive,
    registerStage,
    setStageCount,
    reportImageLoaded,
  };

  return (
    <StagedImageContext.Provider value={value}>
      {children}
    </StagedImageContext.Provider>
  );
};

/**
 * Registers a section's image count and returns whether the section is allowed
 * to load yet. Call once per section (the single owner of that stage's count).
 */
export const useStage = (
  stage: ImageStage,
  imageCount: number,
  completeAfter = imageCount,
) => {
  const { isStageActive, registerStage, setStageCount, reportImageLoaded } =
    useStagedImage();

  useEffect(() => {
    registerStage(stage);
    setStageCount(stage, imageCount, completeAfter);
  }, [stage, imageCount, completeAfter, registerStage, setStageCount]);

  return { active: isStageActive(stage), reportImageLoaded };
};

interface StageImageProps {
  stage: ImageStage;
  src?: string | null;
  alt: string;
  className?: string;
}

/**
 * Renders a skeleton until its stage is active, then requests the image and
 * reports completion so the next stage can begin. A failed image still reports
 * completion so a 404 can never stall the cascade.
 */
export const StageImage = ({ stage, src, alt, className }: StageImageProps) => {
  const { isStageActive, registerStage, reportImageLoaded } = useStagedImage();
  const reported = useRef(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    registerStage(stage);
  }, [stage, registerStage]);

  const active = isStageActive(stage);

  // Report at most once per image.
  const report = useCallback(() => {
    if (reported.current) return;
    reported.current = true;
    reportImageLoaded(stage);
  }, [reportImageLoaded, stage]);

  // If the image is already complete (served from cache) the native `load`
  // event can fire before React attaches the handler, so report here too.
  useEffect(() => {
    if (!active) return;
    const el = imgRef.current;
    if (el && el.complete) report();
  }, [active, report]);

  if (!active || !src) {
    return <div className={`psite-lazy ${className ?? ""}`} aria-hidden="true" />;
  }

  return (
    <img
      ref={imgRef}
      className={className}
      src={resolveImageUrl(src)}
      alt={alt}
      onLoad={report}
      onError={report}
    />
  );
};
